import { useMemo, useRef, useState, type FormEvent } from 'react';
import Select from 'react-select';

type Goal = 'lose-weight' | 'daily-energy' | 'erections' | 'sexual-confidence';
type Choice = 'yes' | 'no' | 'discuss-later';
type HealthContext = 'generally-well' | 'managing-condition' | 'discuss-later';

type AssessmentData = {
  goal: Goal | '';
  name: string;
  email: string;
  region: string;
  healthContext: HealthContext | '';
  hasSpoken: Choice | '';
  notes: string;
  takesMedication: Choice | '';
  medicationDetails: string;
  hasAllergies: Choice | '';
  allergyDetails: string;
};

const stages = ['Goal', 'Location', 'Details', 'Health', 'Care', 'Medication', 'Allergies', 'Review'];

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const stateOptions = states.map((state) => ({ value: state, label: state }));

const initialData: AssessmentData = {
  goal: '',
  name: '',
  email: '',
  region: '',
  healthContext: '',
  hasSpoken: '',
  notes: '',
  takesMedication: '',
  medicationDetails: '',
  hasAllergies: '',
  allergyDetails: '',
};

const goalOptions: Array<{ value: Goal; label: string; description: string }> = [
  { value: 'lose-weight', label: 'Lose weight', description: 'Explore provider-guided options for weight management.' },
  { value: 'daily-energy', label: 'Feel better day to day', description: 'Focus on energy, habits, and sustainable routines.' },
  { value: 'erections', label: 'Improve erections', description: 'Explore discreet support for erectile function.' },
  { value: 'sexual-confidence', label: 'Feel more confident sexually', description: 'Talk through your goals and possible next steps.' },
];

function SelectRow({ label, description, selected, onSelect }: { label: string; description?: string; selected: boolean; onSelect: () => void }) {
  return (
    <button className={'assessment-option' + (selected ? ' is-selected' : '')} type="button" aria-pressed={selected} onClick={onSelect}>
      <span className="assessment-option__control" aria-hidden="true"><span /></span>
      <span className="assessment-option__copy">
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <span className="assessment-option__state">{selected ? 'Selected' : 'Choose'}</span>
    </button>
  );
}

function QuestionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="assessment-question-header">
      <h1 id="assessment-step-title">{title}</h1>
      <p>{subtitle}</p>
    </div>
  );
}

function ChoiceList({ value, onChange }: { value: Choice | ''; onChange: (value: Choice) => void }) {
  return (
    <div className="assessment-options" role="group">
      <SelectRow label="Yes" selected={value === 'yes'} onSelect={() => onChange('yes')} />
      <SelectRow label="No" selected={value === 'no'} onSelect={() => onChange('no')} />
      <SelectRow label="I would rather discuss this later" selected={value === 'discuss-later'} onSelect={() => onChange('discuss-later')} />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <div className="assessment-summary__row"><dt>{label}</dt><dd>{value || 'Not provided'}</dd></div>;
}

function Confirmation({ onClose }: { onClose: () => void }) {
  return (
    <main className="assessment-main">
      <section className="assessment-confirmation" aria-labelledby="confirmation-title">
        <span className="assessment-confirmation__mark" aria-hidden="true">✓</span>
        <p className="assessment-confirmation__eyebrow">Assessment complete</p>
        <h1 id="confirmation-title">You have a clearer place to begin.</h1>
        <p>Your answers have been organized into a simple care summary. No diagnosis or treatment decision has been made.</p>
        <button className="button button--dark" type="button" onClick={onClose}>Return to VT.co</button>
      </section>
    </main>
  );
}

export default function AssessmentFlow({ onExit: onClose }: { onExit: () => void }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<AssessmentData>(initialData);
  const [complete, setComplete] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  const update = (changes: Partial<AssessmentData>) => setData((current) => ({ ...current, ...changes }));
  const emailLooksValid = /^\S+@\S+\.\S+$/.test(data.email.trim());
  const canContinue = useMemo(() => [
    Boolean(data.goal),
    Boolean(data.region),
    Boolean(data.name.trim() && emailLooksValid),
    Boolean(data.healthContext),
    Boolean(data.hasSpoken),
    Boolean(data.takesMedication),
    Boolean(data.hasAllergies),
    true,
  ][step], [data, emailLooksValid, step]);

  const changeStep = (next: number) => {
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'auto' });
    requestAnimationFrame(() => titleRef.current?.focus());
  };

  const formatChoice = (value: Choice | '') => value === 'discuss-later' ? 'Discuss later' : value === 'yes' ? 'Yes' : value === 'no' ? 'No' : 'Not provided';
  const goalLabel = goalOptions.find((option) => option.value === data.goal)?.label || 'Not selected';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;
    if (step === stages.length - 1) setComplete(true);
    else changeStep(step + 1);
  };

  if (complete) return <Confirmation onClose={onClose} />;

  return (
    <div className="assessment-app">
      <header className="assessment-header">
        <div className="assessment-header__bar">
          <button className="assessment-header__action" type="button" onClick={() => step ? changeStep(step - 1) : onClose()}>
            <span aria-hidden="true">←</span>{step ? 'Back' : 'Home'}
          </button>
          <button className="wordmark" type="button" onClick={onClose} aria-label="VT.co home">VT.co</button>
          <button className="assessment-header__action assessment-header__action--right" type="button" onClick={onClose}>Exit</button>
        </div>
      </header>

      <main className="assessment-main">
        <div className="assessment-shell">
          <div className="assessment-progress" aria-label={`Step ${step + 1} of ${stages.length}: ${stages[step]}`}>
            <div className="assessment-progress__meta">
              <span>{stages[step]}</span>
              <span>{step + 1} of {stages.length}</span>
            </div>
            <ol aria-hidden="true">
              {stages.map((stage, index) => <li key={stage} className={index <= step ? 'is-complete' : ''} />)}
            </ol>
          </div>

          <form className="assessment-card" onSubmit={submit} aria-labelledby="assessment-step-title">
            <div className="assessment-card__body" ref={titleRef} tabIndex={-1}>
              {step === 0 && <>
                <QuestionHeader title="What is your primary health goal?" subtitle="Choose the one that matters most to you right now." />
                <div className="assessment-options">
                  {goalOptions.map((option) => <SelectRow key={option.value} label={option.label} description={option.description} selected={data.goal === option.value} onSelect={() => update({ goal: option.value })} />)}
                </div>
              </>}

              {step === 1 && <>
                <QuestionHeader title="What state do you live in?" subtitle="We will use this to check whether care is available where you live." />
                <div className="assessment-fields assessment-fields--location">
                  <div className="assessment-field">
                    <label htmlFor="state-of-residence">State of residence</label>
                    <Select
                      inputId="state-of-residence"
                      instanceId="state-of-residence"
                      className="assessment-state-select"
                      classNamePrefix="assessment-state-select"
                      options={stateOptions}
                      value={stateOptions.find((option) => option.value === data.region) ?? null}
                      onChange={(option) => update({ region: option?.value ?? '' })}
                      placeholder="Search or select a state"
                      noOptionsMessage={() => 'No state found'}
                      isSearchable
                    />
                  </div>
                </div>
              </>}

              {step === 2 && <>
                <QuestionHeader title="How can we identify you?" subtitle="Add the details you would use for your care account." />
                <div className="assessment-fields">
                  <label className="assessment-field"><span>Name</span><input value={data.name} onChange={(event) => update({ name: event.target.value })} autoComplete="name" placeholder="Your name" required /></label>
                  <label className="assessment-field"><span>Email address</span><input type="email" value={data.email} onChange={(event) => update({ email: event.target.value })} autoComplete="email" placeholder="you@example.com" required /><small>Used for account and care updates.</small></label>
                </div>
              </>}

              {step === 3 && <>
                <QuestionHeader title="How would you describe your current health?" subtitle="This helps a provider understand your starting point." />
                <div className="assessment-options">
                  <SelectRow label="Generally well" selected={data.healthContext === 'generally-well'} onSelect={() => update({ healthContext: 'generally-well' })} />
                  <SelectRow label="Managing a health condition" selected={data.healthContext === 'managing-condition'} onSelect={() => update({ healthContext: 'managing-condition' })} />
                  <SelectRow label="I would rather discuss this later" selected={data.healthContext === 'discuss-later'} onSelect={() => update({ healthContext: 'discuss-later' })} />
                </div>
              </>}

              {step === 4 && <>
                <QuestionHeader title="Have you discussed this goal with a clinician before?" subtitle="Your answer helps shape what a provider may ask next." />
                <ChoiceList value={data.hasSpoken} onChange={(hasSpoken) => update({ hasSpoken })} />
                <label className="assessment-field assessment-field--followup"><span>Anything useful to add? <em>Optional</em></span><textarea rows={3} value={data.notes} onChange={(event) => update({ notes: event.target.value })} placeholder="Add a short note" /></label>
              </>}

              {step === 5 && <>
                <QuestionHeader title="Are you currently taking any medication?" subtitle="Medication history is an important part of safe care." />
                <ChoiceList value={data.takesMedication} onChange={(takesMedication) => update({ takesMedication })} />
                {data.takesMedication === 'yes' && <label className="assessment-field assessment-field--followup"><span>Medication details <em>Optional</em></span><textarea rows={3} value={data.medicationDetails} onChange={(event) => update({ medicationDetails: event.target.value })} placeholder="List medication names and doses" /></label>}
              </>}

              {step === 6 && <>
                <QuestionHeader title="Do you have any medication allergies?" subtitle="This helps a provider check which options may be appropriate." />
                <ChoiceList value={data.hasAllergies} onChange={(hasAllergies) => update({ hasAllergies })} />
                {data.hasAllergies === 'yes' && <label className="assessment-field assessment-field--followup"><span>Allergy details <em>Optional</em></span><textarea rows={3} value={data.allergyDetails} onChange={(event) => update({ allergyDetails: event.target.value })} placeholder="List medication allergies and reactions" /></label>}
              </>}

              {step === 7 && <>
                <QuestionHeader title="Review your answers" subtitle="Make sure everything looks right before you finish." />
                <dl className="assessment-summary">
                  <SummaryRow label="Primary goal" value={goalLabel} />
                  <SummaryRow label="Name" value={data.name} />
                  <SummaryRow label="Email" value={data.email} />
                  <SummaryRow label="State of residence" value={data.region} />
                  <SummaryRow label="Current health" value={data.healthContext === 'generally-well' ? 'Generally well' : data.healthContext === 'managing-condition' ? 'Managing a health condition' : 'Discuss later'} />
                  <SummaryRow label="Prior clinician conversation" value={formatChoice(data.hasSpoken)} />
                  {data.notes && <SummaryRow label="Additional context" value={data.notes} />}
                  <SummaryRow label="Taking medication" value={formatChoice(data.takesMedication)} />
                  {data.takesMedication === 'yes' && data.medicationDetails && <SummaryRow label="Medication details" value={data.medicationDetails} />}
                  <SummaryRow label="Medication allergies" value={formatChoice(data.hasAllergies)} />
                  {data.hasAllergies === 'yes' && data.allergyDetails && <SummaryRow label="Allergy details" value={data.allergyDetails} />}
                </dl>
              </>}
            </div>

            <footer className="assessment-card__footer">
              <button className="button button--dark assessment-continue" type="submit" disabled={!canContinue}>{step === stages.length - 1 ? 'Finish assessment' : 'Continue'} <span aria-hidden="true">→</span></button>
            </footer>
          </form>
          <p className="assessment-disclaimer">This intake does not provide a diagnosis. A licensed provider determines whether treatment is appropriate.</p>
        </div>
      </main>
    </div>
  );
}
